import app from 'app';
// don't use inline options of shared worker loader - it will create different blob for each runtime-instance
// and make it no longer sharable
import BroadcastSharedWorker from 'worker-loader?worker=SharedWorker!worker/shared';

export default class BroadcastController {
  constructor() {
    this._subscriptions =
      new Map(/* <Event: String, Callbacks: Set<Function>> */);
    this.originalTitle = document.title;

    if (window.BroadcastChannel) {
      this.broadcastChannel = new BroadcastChannel(
        window.tk.context._broadcastChannelName
      );
      this.broadcastChannel.onmessage = this.eventController.bind(this);
    }
    if (window.SharedWorker) {
      this.sharedWorker = new BroadcastSharedWorker();
      this.sharedWorker.port.onmessage = this.eventController.bind(this);
    }

    if (!this.broadcastChannel || !this.sharedWorker) {
      this._disabled = true;
    }

    if (!this._disabled) {
      this.on('sw-acknowledge', (msg) => {
        // console.log(msg);
      });
      this.on('bc-test', this.onTest.bind(this));
      this.on('bc-user-logout', this.onUserLogout.bind(this));
      this.on('bc-user-enter', this.onUserEnter.bind(this));
      this.on('bc-story-animate', this.onStoryAnimate.bind(this));
      this.on('bc-story-report', this.onStoryReport.bind(this));
    }
  }

  eventController(e) {
    const event = (e.data && e.data.type) || null;
    let handled = false;

    try {
      const calls = this._subscriptions.get(event);
      if (calls) {
        for (const call of calls) {
          handled = true;
          call(e.data);
        }
      }
    } catch (bug) {
      app.doctorController.report(`Broadcast.eventController['${event}']`, bug);
    }

    if (!handled) {
      console.debug(`Unhandled broadcast event: "${event}"`);
    }
  }

  emit(type, payload) {
    const eventId = `${~~(Math.random() * 1e9)}-${~~performance.now()}`;
    const message = { eventId, type, payload, sent: Date.now() };

    if (this._disabled) {
      return Promise.resolve();
    } else if (this.sharedWorker && /^sw-/i.test(type)) {
      this.sharedWorker.port.postMessage(message);
      return new Promise((resolve) => {
        const off = this.on('sw-acknowledge', (msg) => {
          if (eventId === msg.eventId) {
            off();
            resolve(msg.result);
          }
        });
      });
    } else if (this.broadcastChannel && /^bc-/i.test(type)) {
      this.broadcastChannel.postMessage(message);
      return Promise.resolve();
    } else {
      return Promise.reject('unhandled');
    }
  }

  on(event, fn) {
    if (this._subscriptions.has(event)) {
      this._subscriptions.get(event).add(fn);
    } else {
      this._subscriptions.set(event, new Set([fn]));
    }

    return () => {
      this.off(event, fn);
    };
  }

  once(event, fn) {
    return this.on(event, (res) => {
      this.off(event, fn);
      fn(res);
    });
  }

  off(event, fn) {
    if (event && fn) {
      this._subscriptions.get(event).delete(fn);
    } else if (event) {
      this._subscriptions.get(event).clear();
    } else {
      this._subscriptions.delete(event);
    }
  }

  onTest(msg) {
    app.notificationController.push({
      text: `Message from other tab: ${msg.payload}`,
      type: 'INFO',
    });
  }

  onUserLogout() {
    // when user performed logout in other tab
    const cpLogout = new _.ColdPromise();
    if (app.rootStore.getters['user/type'].WOCHIT_INTERNAL) {
      if (app.connectionController) {
        app.connectionController.needToReauthenticate();
      }
    } else {
      cpLogout.resolve();
    }

    cpLogout.then(() => {
      app.state.enableNavigation();
      app.navigate2Login({ allowGetBack: true });
    });
  }

  onUserEnter(e) {
    // when user entered the app in another tab ever after login or refresh
    if (app && app.user) {
      const cpLogout = new _.ColdPromise();
      if (e.payload.userId !== app.rootStore.getters['user/id']) {
        if (app.rootStore.getters['user/type'].WOCHIT_INTERNAL) {
          // support and rnd get warning - they know what they doing..
          app.dialog
            .error({
              type: app.dialog.types.CONFIRM_3,
              description: `
                Another user entered in another tab.<br/>
                Being <strong>WOCHIT_INTERNAL</strong> imposes certain obligations.<br/>
                We hope you know what you are doing. 
              `.normalizeSpace(),
              title: 'Double Authentication',
              showContactCsm: false,
              buttonYesText: 'Stay',
              buttonNoText: 'Leave',
            })
            .then((e) => {
              if (e.action) {
                // 'Stay' let it be, don't interfere
              } else {
                // 'Leave' disaster averted
                cpLogout.resolve();
              }
            });
        } else {
          // conventional user kicked out
          cpLogout.resolve();
        }
      }

      cpLogout.then(() => {
        app.navigate2Login();
      });
    }
  }

  onStoryAnimate(e) {
    // when user tryed to start story opened in this tab - animate this tab title
    const storyId = app.storyController.story && app.storyController.story.id;
    if (storyId !== e.payload.storyId) {
      return;
    }

    const te = new app.namespace.Model.TimedExecutor(
      {
        run: (loop) => {
          if (document.title.charAt(0) === '<') {
            document.title = this.originalTitle;
          } else {
            document.title = '< ' + this.originalTitle;
          }
          loop.resolve();
        },
        timeout: [0.05, 0.1, 0.2, 0.3, 0.4],
      },
      { condition: true }
    );
    te.trigger();
    setTimeout(() => {
      document.title = this.originalTitle;
      te.stop();
    }, app.TIME.second(10));
  }

  accountOpenedStories() {
    if (this._disabled) {
      return;
    }

    // NOTE: the code is better understood if each block read from bottom up
    const openedStories = new Set();
    const timeout = new app.namespace.Util.Timer(() => {
      // check accumulated results
      this.off('bc-story-report-feedback');
      this.emit('sw-story-flush').then(() => {
        if (openedStories.size) {
          for (const id of openedStories) {
            this.emit('sw-story-start', { storyId: id });
          }
        }
      });
    }, app.TIME.second(1));

    this.on('bc-story-report-feedback', (e) => {
      // accumulate results
      if (e.payload.storyId) {
        openedStories.add(e.payload.storyId);
      }
    });

    this.emit('bc-story-report').then(() => {
      timeout.start();
    });
  }

  onStoryReport() {
    const storyId = app.storyController.story && app.storyController.story.id;
    this.emit('bc-story-report-feedback', { storyId });
  }
}
